import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { time } from '~/utils';
import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import {
  Business,
  Individual,
  Moment,
  TransactionsFilters,
  Partner,
  Account,
  PnLAccount,
} from '~/types';
import { AddFilterAction } from '~/components';
import BackgroundTaskExport from '../BackgroundTaskExport';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import cx from 'classnames';
import { TextField } from '@material-ui/core';
import { SelectTransactionType } from './components/SelectTransactionType';
import { SelectTransactionStatus } from './components/SelectTransactionStatus';
import { SelectEmployee } from './components/SelectEmployee';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import AccountTableSingleSelect from '~/components/AccountTableSingleSelect/AccountTableSingleSelect';
import { Target } from '~/types/profile';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';
// import { useHandbooksQuery } from '~/hooks/useHandbook';
// import useEventEntity from '~/hooks/eventEntities';

type Profile = Business | Individual | Partner;

interface FiltersProps {
  record: Individual | Business | Partner | Account | PnLAccount;
  resource: 'individuals' | 'businesses' | 'partners' | 'tenant/accounts' | 'accounts';
  target: Target;
}

const Filters = (props: FiltersProps) => {
  const { record, resource, target } = props;
  const filters = useSelector(selectors.getFilters<TransactionsFilters>(resource));
  const [localValues, setLocalValues] = useState(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>(
    Object.keys(filters).filter((k) => {
      return !!(filters as any)[k];
    })
  );
  const listFilterClasses = useListFilterStyles();
  const classes = useStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();

  const t = (key: string): string => translate(`components.TransactionsHistory.${key}`);

  const translateDateTime = (string: string) =>
    translate(`${i18nFilters}.KeyboardDateTimePicker.${string}`);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);
  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });
    debouncedDispatchFilterChange(name, value);
  };

  const handleDateTimeChange = (data: Moment, inputType: string) => {
    setLocalValues({
      ...localValues,
      [inputType]: data,
    });
    debouncedDispatchFilterChange(inputType, data);
  };

  const isAccount = resource === 'accounts';
  const isBusiness = resource === 'businesses' || (isAccount && target === 'businesses');
  // const isIndividual = resource === 'individuals' || (isAccount && target === 'individuals');
  const isPartner = resource === 'partners' || (isAccount && target === 'partners');
  const isTenant = resource === 'tenant/accounts' || (isAccount && target === 'tenant');

  const additionalFilters = ['accountNumber'];
  const showAdditionalFilters =
    resource === 'businesses' || resource === 'individuals' || resource === 'partners';
  const allFilters = [
    'operationType',
    'status',
    // 'transactionId', // not working on backend
    ...(!isTenant ? ['referenceId'] : []), // hiding filters for tenant as it's not supported on backend
    ...(showAdditionalFilters ? additionalFilters : []),
  ];

  const handleSelectInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });
    dispatchFilterChange(name, value);
  };

  const hideFilter = (filter: string) => {
    dispatchFilterChange(filter, '');
    setAddedFilters(addedFilters.filter((f) => f !== filter));
  };

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    dispatch(
      filterChange({
        resource,
        filters: {
          fromCreatedAt: time().subtract(1, 'months'),
          toCreatedAt: time(),
          accountId: isTenant || isAccount ? record.id : null,
          profileId:
            (isTenant || isAccount) && 'profileId' in record ? record?.profileId : record.id,
        },
      })
    );
    return () => {
      dispatch(cleanupFilters({ resource }));
    };
  }, [dispatch, isAccount, isPartner, isTenant, record, resource]);

  // const { data: entity, isLoading: isLoadingEntity } = useEventEntity(
  //   { id: (record as Account)?.profileId, type: (record as Account)?.profileType },
  //   isAccount && !!(record as Account)?.profileId && !!(record as Account)?.profileType
  // );

  // const solutionId = !isTenant
  //   ? isAccount
  //     ? entity?.solutionId
  //     : (record as Profile)?.solutionId
  //   : undefined;
  // const { data: handbook } = useHandbooksQuery(solutionId);
  // const currencies = handbook?.currencies;

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'operationType':
        return (
          <SelectTransactionType
            onChange={handleSelectInput}
            value={localValues.operationType || ''}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'status':
        return (
          <SelectTransactionStatus
            onChange={handleSelectInput}
            value={localValues.status || ''}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'referenceId':
        return (
          <TextField
            label={t('referenceId')}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
            onChange={debouncedHandleInput}
            value={localValues.referenceId || ''}
            name='referenceId'
          />
        );
      case 'accountNumber':
        return (
          <AccountTableSingleSelect
            resource={
              isPartner
                ? 'partners'
                : isTenant
                ? 'tenant/accounts'
                : isBusiness
                ? 'businesses'
                : 'individuals'
            }
            profileId={isAccount ? (record as Account)?.profileId : record.id}
            label={t('accountNumber')}
            value={localValues.accountNumber || ''}
            onChange={(account) => {
              setLocalValues({ ...localValues, accountNumber: account.number });
              dispatchFilterChange('accountNumber', account.number);
            }}
            onClear={() => {
              setLocalValues({ ...localValues, accountNumber: undefined });
              dispatchFilterChange('accountNumber', '');
            }}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      // case 'amount':
      //   return (
      //     <TextField
      //       label={t('amount')}
      //       className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
      //       onChange={debouncedHandleInput}
      //       value={localValues.amount}
      //       name='amount'
      //     />
      //   );
      // keep it for future use
      // case 'otherAccountNumber':
      //   return (
      //     <TextField
      //       label={t('otherAccountNumber')}
      //       className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
      //       onChange={debouncedHandleInput}
      //       value={localValues.otherAccountNumber}
      //       name='otherAccountNumber'
      //     />
      //   );
      // case 'transactionId':
      //   return (
      //     <TextField
      //       label={t('transactionId')}
      //       className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
      //       onChange={debouncedHandleInput}
      //       value={localValues.transactionId}
      //       name='transactionId'
      //     />
      //   );
      // case 'currency':
      //   return (
      //     <CurrencyAutocomplete
      //       label={t('currency')}
      //       className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
      //       onChange={debouncedHandleInput}
      //       value={localValues.currency}
      //       filterOptions={(all) => all.filter((c) => currencies?.find((hc) => hc.id === c))}
      //       name='currency'
      //       disabled={isLoadingEntity}
      //     />
      //   );
      default:
        return null;
    }
  };

  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (component) {
        return (
          <div className={listFilterClasses.flexContainer} key={filter}>
            {component}
            <IconButton
              aria-label='delete'
              className={listFilterClasses.deleteIcon}
              onClick={() => hideFilter(filter)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      } else {
        return null;
      }
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={listFilterClasses.inputsGroup}>
            <DateTimePicker
              className={cx(
                listFilterClasses.formControl,
                listFilterClasses.formControlLong,
                listFilterClasses.datePickers
              )}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={translateDateTime('from')}
              value={localValues.fromCreatedAt}
              onChange={(date) => handleDateTimeChange(date as Moment, 'fromCreatedAt')}
            />
            <DateTimePicker
              className={cx(
                listFilterClasses.formControl,
                listFilterClasses.formControlLong,
                listFilterClasses.datePickers
              )}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={translateDateTime('to')}
              value={localValues.toCreatedAt}
              onChange={(date) => handleDateTimeChange(date as Moment, 'toCreatedAt')}
            />
            {isBusiness && (
              <SelectEmployee
                businessId={isAccount ? (record as Account)?.profileId : record.id}
                name='employeeId'
                label={translate('components.TransactionsHistory.Filters.employee')}
                onChange={handleSelectInput}
                value={localValues.employeeId || ''}
                className={cx(listFilterClasses.formControl, listFilterClasses.formControlLong)}
              />
            )}
            {renderAddedFilters()}
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: translate(`${i18nFilters}.AddedFilters.${filterKey}`),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
            <BackgroundTaskExport type='BALANCE_TRANSACTION_EXPORT' queryArguments={filters} />
          </div>
        </div>
      )}
    </Fragment>
  );
};

const useStyles = makeStyles(() => {
  return {
    rightSide: { minWidth: 250, textAlign: 'right' },
  };
});

const i18nFilters = 'components.TransactionsHistory.Filters';

export default Filters;
